import type { AdvancedReportsAgentPerformanceRow } from 'src/api/generated';

export interface AgentPerformanceCsvLabels {
  index: string;
  agent: string;
  connectedEffective: string;
  connectedVoicemail: string;
  noAnswer: string;
  rejected: string;
  total: string;
  averageDuration: string;
  minutesTalked: string;
  leadCount: string;
  /** Localized header for unique-lead contactability. */
  leadContactability: string;
  /** Localized header for call-level contactability. */
  callContactability: string;
  callsPerLead: string;
  expertise: string;
  confidence: string;
  objections: string;
  closing: string;
  totalPerformance: string;
  team: string;
}

const csvEscape = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);
  if (!/[",\n]/.test(stringValue)) {
    return stringValue;
  }

  return `"${stringValue.replace(/"/g, '""')}"`;
};

const formatAgentPerformanceAverageDurationCsv = (
  value: number | null | undefined
) => {
  if (value === null || value === undefined) {
    return '';
  }

  return value / 60;
};

export const buildAgentPerformanceCsvContent = (
  rows: AdvancedReportsAgentPerformanceRow[],
  showContactability: boolean,
  labels: AgentPerformanceCsvLabels
) => {
  const headers = [
    labels.index,
    labels.agent,
    labels.connectedEffective,
    labels.connectedVoicemail,
    labels.noAnswer,
    labels.rejected,
    labels.total,
    labels.averageDuration,
    labels.minutesTalked,
    labels.leadCount,
    ...(showContactability
      ? [labels.leadContactability, labels.callContactability]
      : []),
    labels.callsPerLead,
    labels.expertise,
    labels.confidence,
    labels.objections,
    labels.closing,
    labels.totalPerformance,
    labels.team,
  ];

  const csvRows = rows.map((row, index) => [
    index + 1,
    row.agentName,
    row.connectedCalls,
    row.voicemailCalls,
    row.noAnswerCalls,
    row.rejectedCalls,
    row.totalCalls,
    formatAgentPerformanceAverageDurationCsv(row.averageDurationSeconds),
    row.minutesTalked,
    row.leadCount,
    ...(showContactability
      ? [row.contactabilityPercent, row.callContactabilityPercent]
      : []),
    row.callsPerLead,
    row.expertise,
    row.confidenceGenerated,
    row.objectionHandling,
    row.callClosing,
    row.performance,
    row.teamName,
  ]);

  return [headers, ...csvRows]
    .map((row) => row.map((value) => csvEscape(value)).join(','))
    .join('\n');
};

export const downloadCsvFile = (filename: string, content: string) => {
  const blob = new Blob([`\uFEFF${content}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
